import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Tabs, Form, Input, Button, message, Alert, Descriptions, Divider,
  Typography, Tag, Avatar, Modal, Space, Spin, Result
} from 'antd';
import {
  UserOutlined, LockOutlined, SaveOutlined, WechatOutlined,
  CheckCircleOutlined, DisconnectOutlined, LinkOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

// 工具函数：获取带 token 的请求头
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

const Profile = () => {
  const { user, setUserLogin } = useAuth();
  const [passwordForm] = Form.useForm();
  const [bindPwdForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 微信绑定相关状态
  const [wechatStatus, setWechatStatus] = useState({ bound: false, wechat_nickname: null, wechat_avatar: null });
  const [wechatStatusLoading, setWechatStatusLoading] = useState(false);
  const [bindingWechat, setBindingWechat] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [bindState, setBindState] = useState(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [bindSuccess, setBindSuccess] = useState(false);
  const pollingRef = useRef(null);

  // 账号密码绑定相关状态
  const [bindPwdLoading, setBindPwdLoading] = useState(false);
  const [bindPwdError, setBindPwdError] = useState(null);
  const [passwordStatus, setPasswordStatus] = useState({ configured: false, username: '' });

  const canConfigureLogin = ['teacher', 'school_admin', 'platform_admin', 'super_admin', 'admin', 'owner', 'principal'].includes(user?.role) || user?.username === 'admin';

  // 加载微信绑定状态
  const loadWechatStatus = async () => {
    if (!canConfigureLogin) return;
    setWechatStatusLoading(true);
    try {
      const res = await axios.get('/api/v2/auth/bind-wechat/status', { headers: getAuthHeaders() });
      if (res.data.success) {
        setWechatStatus(res.data.data);
      }
    } catch {
      // 静默失败
    } finally {
      setWechatStatusLoading(false);
    }
  };

  const loadPasswordStatus = async () => {
    if (!canConfigureLogin) return;
    try {
      const res = await axios.get('/api/v2/auth/password-status', { headers: getAuthHeaders() });
      if (res.data.success) {
        const nextStatus = res.data.data || { configured: false, username: '' };
        setPasswordStatus(nextStatus);
        if (nextStatus.username) bindPwdForm.setFieldsValue({ username: nextStatus.username });
      }
    } catch {
      // 保留表单可用，由提交接口返回明确错误
    }
  };

  useEffect(() => {
    loadWechatStatus();
    loadPasswordStatus();
  }, [canConfigureLogin]);

  // 停止轮询
  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  // 关闭二维码弹窗
  const handleCloseQrModal = () => {
    stopPolling();
    setQrModalVisible(false);
    setQrCodeUrl(null);
    setBindState(null);
    setBindingWechat(false);
  };

  // 开始微信绑定：获取二维码
  const handleInitWechatBind = async () => {
    setBindingWechat(true);
    setBindSuccess(false);
    try {
      // step1: 在服务端创建绑定会话，拿到 bindState
      const res = await axios.post('/api/v2/auth/bind-wechat/init', {}, { headers: getAuthHeaders() });
      if (!res.data.success) {
        message.error(res.data.message || '发起绑定失败');
        setBindingWechat(false);
        return;
      }
      const { state } = res.data.data;
      setBindState(state);

      // step2: 用该 state 生成二维码（小程序扫码时会携带 state 参数）
      const qrRes = await axios.post('/api/v2/auth/wechat/qrcode', {
        type: 'bind_wechat',
        state             // ← 告知二维码内的 state，小程序扫码后用 scan-confirm 上报
      });
      if (qrRes.data.success) {
        setQrCodeUrl(qrRes.data.qrCodeUrl);
        setQrModalVisible(true);

        // step3: 轮询 bind-wechat/status，等待 server 将 openid 写入当前账号
        stopPolling();
        pollingRef.current = setInterval(async () => {
          try {
            const statusRes = await axios.get('/api/v2/auth/bind-wechat/status', { headers: getAuthHeaders() });
            if (statusRes.data.success && statusRes.data.data.bound) {
              stopPolling();
              setBindSuccess(true);
              setWechatStatus(statusRes.data.data);
              message.success(`微信绑定成功！已绑定：${statusRes.data.data.wechat_nickname || '微信用户'}`);
              setTimeout(() => handleCloseQrModal(), 2000);
            }
          } catch { /* 静默 */ }
        }, 2000);
      } else {
        message.error('生成二维码失败，请重试');
      }
    } catch (err) {
      message.error(err.response?.data?.message || '操作失败，请稍后重试');
    } finally {
      setBindingWechat(false);
    }
  };

  // 解绑微信
  const handleUnbindWechat = () => {
    Modal.confirm({
      title: '确认解绑微信',
      content: '解绑后将无法使用微信扫码登录，请确认您已设置账号密码。',
      okText: '确认解绑',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await axios.post('/api/v2/auth/bind-wechat/unbind', {}, { headers: getAuthHeaders() });
          if (res.data.success) {
            message.success('微信已解绑');
            setWechatStatus({ bound: false, wechat_nickname: null, wechat_avatar: null });
          } else {
            message.error(res.data.message || '解绑失败');
          }
        } catch (err) {
          message.error(err.response?.data?.message || '解绑失败');
        }
      }
    });
  };

  // 修改密码
  const handleChangePassword = async (values) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.changePassword(values.currentPassword, values.newPassword);
      if (response.data.success) {
        message.success('密码修改成功');
        passwordForm.resetFields();
      } else {
        setError(response.data.message || '密码修改失败');
      }
    } catch (err) {
      setError(err.response?.data?.message || '密码修改失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  // 绑定/设置账号密码（微信登录的教师）
  const handleBindPassword = async (values) => {
    setBindPwdLoading(true);
    setBindPwdError(null);
    try {
      const res = await axios.post('/api/v2/auth/bind-password', {
        username: values.username,
        password: values.password,
        ...(passwordStatus.configured ? { currentPassword: values.currentPassword } : {})
      }, { headers: getAuthHeaders() });
      if (res.data.success) {
        const nextUsername = res.data.data?.username || values.username;
        const token = localStorage.getItem('token');
        if (user && token) {
          setUserLogin({
            ...user,
            username: nextUsername,
            name: user.name === user.username ? nextUsername : user.name
          }, token);
        }
        setPasswordStatus({ configured: true, username: nextUsername });
        message.success(res.data.message || '设置成功');
        bindPwdForm.setFieldsValue({
          username: nextUsername,
          currentPassword: undefined,
          password: undefined,
          confirmPassword: undefined
        });
      } else {
        setBindPwdError(res.data.message || '设置失败');
      }
    } catch (err) {
      setBindPwdError(err.response?.data?.message || '设置失败，请稍后重试');
    } finally {
      setBindPwdLoading(false);
    }
  };

  // 微信绑定面板
  const WechatBindPanel = () => (
    <div>
      <Spin spinning={wechatStatusLoading}>
        <div style={{ padding: '8px 0 24px' }}>
          {wechatStatus.bound ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px', background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
              {wechatStatus.wechat_avatar
                ? <Avatar size={56} src={wechatStatus.wechat_avatar} />
                : <Avatar size={56} icon={<WechatOutlined />} style={{ background: '#07c160' }} />
              }
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                  已绑定微信
                </div>
                {wechatStatus.wechat_nickname && (
                  <div style={{ color: '#555', marginTop: 4 }}>微信昵称：{wechatStatus.wechat_nickname}</div>
                )}
              </div>
              <Button
                danger
                icon={<DisconnectOutlined />}
                style={{ marginLeft: 'auto' }}
                onClick={handleUnbindWechat}
              >
                解绑微信
              </Button>
            </div>
          ) : (
            <div style={{ padding: '20px', background: '#fffbe6', borderRadius: 8, border: '1px solid #ffe58f' }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>
                <WechatOutlined style={{ color: '#07c160', marginRight: 6 }} />
                尚未绑定微信
              </div>
              <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                绑定微信后可直接扫码登录，无需输入账号密码。
              </Paragraph>
              <Button
                type="primary"
                icon={<LinkOutlined />}
                onClick={handleInitWechatBind}
                loading={bindingWechat}
                style={{ background: '#07c160', border: 'none' }}
              >
                扫码绑定微信
              </Button>
            </div>
          )}
        </div>
      </Spin>

      {/* 微信二维码弹窗 */}
      <Modal
        title={
          <span><WechatOutlined style={{ color: '#07c160', marginRight: 8 }} />扫码绑定微信</span>
        }
        open={qrModalVisible}
        onCancel={handleCloseQrModal}
        footer={[
          <Button key="refresh" icon={<ReloadOutlined />} onClick={handleInitWechatBind}>
            刷新二维码
          </Button>,
          <Button key="cancel" onClick={handleCloseQrModal}>取消</Button>
        ]}
        centered
        width={380}
      >
        {bindSuccess ? (
          <Result status="success" title="绑定成功！" subTitle="您的微信已成功绑定到账号" />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Paragraph type="secondary">请用微信扫描下方二维码完成绑定</Paragraph>
            {qrCodeUrl ? (
              <div style={{
                display: 'inline-block', padding: 16, background: '#fff',
                border: '1px solid #e8e8e8', borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <img src={qrCodeUrl} alt="绑定二维码" style={{ width: 200, height: 200, display: 'block' }} />
              </div>
            ) : (
              <Spin tip="生成二维码中..." style={{ display: 'block', padding: 40 }} />
            )}
            <div style={{ marginTop: 16, fontSize: 12, color: '#999' }}>
              <p>• 打开微信 → 扫一扫</p>
              <p>• 二维码5分钟内有效</p>
              <p>• 扫码后在小程序内确认绑定</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );

  // 账号密码绑定面板（支持教师和管理员使用同一账号绑定两种登录方式）
  const BindPasswordPanel = () => (
    <div>
      <Alert
        message={passwordStatus.configured ? '修改管理后台账号密码' : '设置管理后台账号密码'}
        description={passwordStatus.configured
          ? '账号已经设置过密码。为保护账号安全，保存修改前需要验证当前密码。'
          : '设置后即可使用同一个教师账号，通过用户名和密码登录后台管理系统。'}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      {bindPwdError && (
        <Alert type="error" message={bindPwdError} showIcon style={{ marginBottom: 16 }} />
      )}
      <Form form={bindPwdForm} layout="vertical" onFinish={handleBindPassword}>
        <Form.Item
          name="username"
          label="登录用户名"
          rules={[
            { required: true, message: '请输入用户名' },
            { pattern: /^[A-Za-z0-9][A-Za-z0-9_.-]{2,29}$/, message: '请输入3-30位字母、数字或 _ . -' }
          ]}
        >
          <Input prefix={<UserOutlined />} placeholder="设置登录用户名" />
        </Form.Item>
        {passwordStatus.configured && (
          <Form.Item
            name="currentPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="修改前请验证当前密码" />
          </Form.Item>
        )}
        <Form.Item
          name="password"
          label={passwordStatus.configured ? '新密码' : '登录密码'}
          rules={[
            { required: true, message: '请输入密码' },
            { min: 8, message: '密码至少8位' },
            { max: 128, message: '密码最多128位' }
          ]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder="设置登录密码" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="确认密码"
          dependencies={['password']}
          rules={[
            { required: true, message: '请确认密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) return Promise.resolve();
                return Promise.reject(new Error('两次输入的密码不一致'));
              }
            })
          ]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder="再次输入密码" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={bindPwdLoading}>
            保存账号密码
          </Button>
        </Form.Item>
      </Form>
    </div>
  );

  // 构建标签页
  const items = [
    {
      label: '个人信息',
      key: 'info',
      children: (
        <div>
          <Descriptions bordered column={1} labelStyle={{ width: '150px', fontWeight: 'bold' }}>
            <Descriptions.Item label="用户名">{user?.username || '-'}</Descriptions.Item>
            <Descriptions.Item label="姓名">{user?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="邮箱">{user?.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="角色">
              {user?.role === 'super_admin' ? '超级管理员'
                : user?.role === 'school_admin' ? '学校管理员'
                  : user?.role === 'teacher' ? '教师'
                    : user?.role || '-'}
            </Descriptions.Item>
            {user?.schoolId && (
              <Descriptions.Item label="所属学校">学校 ID: {user.schoolId}</Descriptions.Item>
            )}
          </Descriptions>
          <Divider />
          <Text type="secondary">* 如需修改个人信息，请联系系统管理员</Text>
        </div>
      )
    },
    {
      label: '修改密码',
      key: 'password',
      children: (
        <div>
          {error && (
            <Alert message="错误" description={error} type="error" showIcon style={{ marginBottom: 20 }} />
          )}
          <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
            <Form.Item name="currentPassword" label="当前密码" rules={[{ required: true, message: '请输入当前密码' }]}>
              <Input.Password prefix={<LockOutlined />} placeholder="当前密码" />
            </Form.Item>
            <Form.Item
              name="newPassword"
              label="新密码"
              rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '密码长度至少6个字符' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="新密码" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="确认新密码"
              rules={[
                { required: true, message: '请再次输入新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  }
                })
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="确认新密码" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                保存修改
              </Button>
            </Form.Item>
          </Form>
        </div>
      )
    }
  ];

  // 教师和管理员额外显示微信绑定和账号密码设置
  if (canConfigureLogin) {
    items.push({
      label: (
        <span>
          <WechatOutlined />
          微信绑定
          {wechatStatus.bound && (
            <Tag color="green" style={{ marginLeft: 6, fontSize: 11 }}>已绑定</Tag>
          )}
        </span>
      ),
      key: 'wechat',
      children: <WechatBindPanel />
    });

    items.push({
      label: (
        <span>
          <LinkOutlined />
          账号密码
        </span>
      ),
      key: 'bind_pwd',
      children: <BindPasswordPanel />
    });
  }

  return (
    <div>
      <Title level={2}>个人资料</Title>
      <Card>
        <Tabs defaultActiveKey="info" items={items} />
      </Card>
    </div>
  );
};

export default Profile;
